// Shared delivery estimate rendering (rates come from snippets/delivery-rates.liquid)
// Wrapped in a function because this file can be loaded twice on one page (product card + cart drawer).
(function () {
const DeliveryEstimateRenderer = {
  price(pence) {
    return `£${(pence / 100).toFixed(2)}`;
  },

  bandPrice(service, kg) {
    const band = service.bands.find(([maxKg]) => kg <= maxKg);
    return band ? band[1] : null;
  },

  // kg: total weight, basket: total price in pence
  render(el, config, kg, basket) {
    const tilesEl = el.querySelector('.delivery-estimate__tiles');
    const nudgeEl = el.querySelector('.delivery-estimate__nudge');
    // weight 0 (no weight set) falls in the first band, as it does at Shopify's checkout
    const standard = kg >= 0 ? this.bandPrice(config.standard, kg) : null;
    const nextDay = kg >= 0 ? this.bandPrice(config.next_day, kg) : null;

    if (standard === null || nextDay === null) {
      tilesEl.innerHTML = '<p class="delivery-estimate__fallback">Delivery cost <strong>calculated at checkout</strong></p>';
      nudgeEl.hidden = true;
      return;
    }

    const qualifies = kg < config.free_under_kg;
    const free = qualifies && basket >= config.free_over_pence;
    const tile = (service, priceHtml) =>
      `<div class="delivery-tile"><span class="delivery-tile__name">${service.label}</span>` +
      `<span class="delivery-tile__price">${priceHtml}</span>` +
      `<span class="delivery-tile__days">${service.days}</span></div>`;

    const stdPrice = free
      ? `<s>${this.price(standard)}</s> <span class="delivery-tile__free">FREE</span>`
      : this.price(standard);
    tilesEl.innerHTML = tile(config.standard, stdPrice) + tile(config.next_day, this.price(nextDay));

    if (free) {
      nudgeEl.textContent = '✓ You qualify for free national standard delivery';
      nudgeEl.hidden = false;
    } else if (qualifies) {
      nudgeEl.innerHTML = `Spend <strong>${this.price(config.free_over_pence - basket)}</strong> more for <strong>free</strong> national standard delivery`;
      nudgeEl.hidden = false;
    } else {
      nudgeEl.hidden = true;
    }
  },
};

if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        this.config = JSON.parse(this.querySelector('[data-config]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.variantId = this.dataset.variantId;
        this.sectionId = this.dataset.sectionId;
        this.quantity = 1;

        const input = document.getElementById(`Quantity-${this.sectionId}`);
        if (input) {
          this.quantity = parseInt(input.value, 10) || 1;
          ['input', 'change'].forEach((evt) =>
            input.addEventListener(evt, () => {
              this.quantity = parseInt(input.value, 10) || 1;
              this.render();
            })
          );
        }

        this.subscribeToVariantChange();
        this.render();
      }

      subscribeToVariantChange() {
        if (!window.subscribe || !window.PUB_SUB_EVENTS) return setTimeout(() => this.subscribeToVariantChange(), 50);
        subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
          if (!e.data || e.data.sectionId !== this.sectionId || !e.data.variant) return;
          this.variantId = String(e.data.variant.id);
          this.render();
        });
      }

      render() {
        const variant = this.variants[this.variantId];
        if (!variant || !variant.available) {
          this.hidden = true;
          return;
        }
        this.hidden = false;
        DeliveryEstimateRenderer.render(
          this,
          this.config,
          (variant.weight * this.quantity) / 1000,
          variant.price * this.quantity
        );
        // lets the same-day delivery block (a separate element) work out its free-delivery threshold
        this.basketPence = variant.price * this.quantity;
        window.thBasketPence = () => this.basketPence;
        document.dispatchEvent(new CustomEvent('th:basket'));
      }
    }
  );
}

// Cart drawer: whole-cart estimate. The drawer HTML is re-rendered on every cart change,
// so this element is re-created with fresh data-weight / data-total each time.
if (!customElements.get('cart-delivery-estimate')) {
  customElements.define(
    'cart-delivery-estimate',
    class CartDeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const config = JSON.parse(this.querySelector('[data-config]').textContent);
        DeliveryEstimateRenderer.render(
          this,
          config,
          (parseFloat(this.dataset.weight) || 0) / 1000,
          parseInt(this.dataset.total, 10) || 0
        );
        this.setupSameDay();
      }

      // Each item's stores and click & collect rows come from the same pickup-availability
      // section the product page uses.
      async infoForVariant(id) {
        const key = 'th_cart_pickup_' + id;
        try {
          const hit = JSON.parse(sessionStorage.getItem(key) || 'null');
          if (hit && Date.now() - hit.t < 120000) return hit.v;
        } catch (e) {}
        let info = { stores: [], rows: [] };
        try {
          const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
          const text = await (await fetch(`${root}variants/${id}/?section_id=pickup-availability`)).text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const sd = doc.querySelector('same-day-delivery');
          info.stores = sd ? (sd.dataset.stores || '').split(',').filter(Boolean) : [];
          info.rows = [...doc.querySelectorAll('.pickup-row')].map((r) => ({
            name: r.querySelector('.pickup-row__name').textContent.trim(),
            time: r.querySelector('.pickup-row__time').textContent.trim(),
            transfer: r.classList.contains('pickup-row--transfer'),
          }));
        } catch (e) {}
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: info })); } catch (e) {}
        return info;
      }

      hours(text) {
        const m = /(\d+)\s*hour/i.exec(text || '');
        return m ? parseInt(m[1], 10) : 0;
      }

      renderPickup(infos) {
        const box = this.querySelector('.cart-pickup');
        if (!box) return;
        // a store is offered if every item has a row for it; time is the slowest item's time
        const names = infos[0].rows.map((r) => r.name);
        const out = names
          .filter((n) => infos.every((i) => i.rows.some((r) => r.name === n)))
          .map((n) => {
            const rows = infos.map((i) => i.rows.find((r) => r.name === n));
            const slowest = rows.reduce((a, b) => (this.hours(b.time) > this.hours(a.time) ? b : a));
            return { name: n, time: slowest.time, transfer: rows.some((r) => r.transfer) };
          });
        if (!out.length) {
          box.hidden = true;
          return;
        }
        box.querySelector('.cart-pickup__rows').innerHTML = out
          .map((r) =>
            `<div class="pickup-row${r.transfer ? ' pickup-row--transfer' : ''}"><span class="pickup-row__dot" aria-hidden="true"></span>` +
            `<span class="pickup-row__name">${r.name}</span><span class="pickup-row__time">${r.time}</span></div>`
          )
          .join('');
        box.hidden = false;
      }

      async setupSameDay() {
        const sameDay = this.querySelector('same-day-delivery');
        const ids = [...new Set((this.dataset.variantIds || '').split(',').filter(Boolean))];
        if (!ids.length) return;
        const infos = await Promise.all(ids.map((id) => this.infoForVariant(id)));
        this.renderPickup(infos);
        if (!sameDay) return;
        const common = infos.map((i) => i.stores).reduce((a, b) => a.filter((n) => b.includes(n)));
        await customElements.whenDefined('same-day-delivery');
        sameDay.dataset.stores = common.join(',') + (common.length ? ',' : '');
        sameDay.init();
      }
    }
  );
}
})();
